import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, Loader2, Inbox, Users, Sun } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { adminFetch, verifyAdmin } from "../lib/api";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("nsl_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [solar, setSolar] = useState([]);

  const loadData = async (tk) => {
    setLoading(true);
    try {
      const [e, a, s] = await Promise.all([
        adminFetch("/enquiries", tk),
        adminFetch("/network-applications", tk),
        adminFetch("/solar-estimates", tk),
      ]);
      setEnquiries(e);
      setApplications(a);
      setSolar(s);
      setAuthed(true);
    } catch {
      toast.error("Invalid admin token");
      localStorage.removeItem("nsl_admin_token");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData(token);
    // eslint-disable-next-line
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyAdmin(input);
      localStorage.setItem("nsl_admin_token", input);
      setToken(input);
      await loadData(input);
    } catch {
      toast.error("Invalid admin token");
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-cream px-6" data-testid="admin-login">
        <form onSubmit={login} className="bg-white border border-stone-200 rounded-2xl p-8 w-full max-w-md shadow-sm">
          <Lock className="w-10 h-10 text-brand-accent mx-auto mb-4" />
          <h1 className="font-outfit text-2xl font-semibold text-brand text-center mb-1">Admin access</h1>
          <p className="font-figtree text-sm text-stone-500 text-center mb-6">Enter the admin token to view submissions.</p>
          <input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin token" className="w-full rounded-xl border border-stone-300 px-4 py-3.5 mb-4 outline-none focus:ring-2 focus:ring-brand-accent" />
          <button data-testid="admin-login-button" disabled={loading} className="w-full bg-brand-accent text-white rounded-full px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const data = tab === "enquiries" ? enquiries : tab === "solar" ? solar : applications;

  return (
    <div className="min-h-screen pt-28 pb-16 bg-brand-cream" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h1 className="font-outfit text-3xl font-bold text-brand">Submissions</h1>
            <button data-testid="admin-logout-button" onClick={() => { localStorage.removeItem("nsl_admin_token"); setAuthed(false); setToken(""); }} className="text-sm text-stone-500 hover:text-brand-accent">Log out</button>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            <button data-testid="admin-tab-enquiries" onClick={() => setTab("enquiries")} className={`rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 ${tab === "enquiries" ? "bg-brand text-white" : "bg-white text-brand border border-stone-200"}`}>
              <Inbox className="w-4 h-4" /> Survey enquiries ({enquiries.length})
            </button>
            <button data-testid="admin-tab-solar" onClick={() => setTab("solar")} className={`rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 ${tab === "solar" ? "bg-brand text-white" : "bg-white text-brand border border-stone-200"}`}>
              <Sun className="w-4 h-4" /> Solar estimates ({solar.length})
            </button>
            <button data-testid="admin-tab-applications" onClick={() => setTab("applications")} className={`rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 ${tab === "applications" ? "bg-brand text-white" : "bg-white text-brand border border-stone-200"}`}>
              <Users className="w-4 h-4" /> Installer applications ({applications.length})
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-accent" /></div>
          ) : data.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center text-stone-500 font-figtree">No submissions yet.</div>
          ) : (
            <div className="space-y-4">
              {tab === "enquiries"
                ? enquiries.map((e) => (
                    <div key={e.id} data-testid="enquiry-row" className="bg-white border border-stone-200 rounded-2xl p-6">
                      <div className="flex flex-wrap justify-between gap-2 mb-2">
                        <p className="font-outfit font-semibold text-brand">{e.name} · {e.postcode}</p>
                        <span className="text-xs text-stone-400">{new Date(e.created_at).toLocaleString()}</span>
                      </div>
                      <p className="font-figtree text-sm text-stone-600">{e.email} · {e.phone}</p>
                      <p className="font-figtree text-sm text-stone-600 mt-1">Measures: {e.measures?.join(", ") || "—"} · {e.property_type || "—"} · {e.homeowner || "—"}</p>
                      {e.message && <p className="font-figtree text-sm text-stone-500 mt-2 italic">"{e.message}"</p>}
                    </div>
                  ))
                : tab === "solar"
                ? solar.map((s) => (
                    <div key={s.id} data-testid="solar-row" className="bg-white border border-stone-200 rounded-2xl p-6">
                      <div className="flex flex-wrap justify-between gap-2 mb-2">
                        <p className="font-outfit font-semibold text-brand">{s.name} · {s.occupancy || "—"}</p>
                        <span className="text-xs text-stone-400">{new Date(s.created_at).toLocaleString()}</span>
                      </div>
                      <p className="font-figtree text-sm text-stone-600">{s.email} · {s.phone}{s.address ? ` · ${s.address}` : ""}</p>
                      <p className="font-figtree text-sm text-stone-600 mt-1">
                        {s.roof_area_m2} m² roof · {s.panel_count} panels · {s.system_kwp} kWp · {s.annual_generation_kwh?.toLocaleString()} kWh/yr
                      </p>
                      <p className="font-figtree text-sm text-stone-600 mt-1">
                        Battery: {s.battery_option || "none"}{s.battery_location ? ` (${s.battery_location})` : ""} · Est. cost £{s.estimated_cost?.toLocaleString()} · Saving £{s.annual_savings?.toLocaleString()}/yr{s.payback_years ? ` · ~${s.payback_years} yr payback` : ""}
                      </p>
                    </div>
                  ))
                : applications.map((a) => (
                    <div key={a.id} data-testid="application-row" className="bg-white border border-stone-200 rounded-2xl p-6">
                      <div className="flex flex-wrap justify-between gap-2 mb-2">
                        <p className="font-outfit font-semibold text-brand">{a.company_name} · {a.trade}</p>
                        <span className="text-xs text-stone-400">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                      <p className="font-figtree text-sm text-stone-600">{a.contact_name} · {a.email} · {a.phone}</p>
                      <p className="font-figtree text-sm text-stone-600 mt-1">Coverage: {a.coverage_area} · Accreditations: {a.accreditations || "—"}</p>
                      {a.message && <p className="font-figtree text-sm text-stone-500 mt-2 italic">"{a.message}"</p>}
                    </div>
                  ))}
            </div>
          )}
        </Reveal>
      </div>
    </div>
  );
}
